import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faClock, faCalendarAlt, faEdit } from '@fortawesome/free-solid-svg-icons';
import './EditCampaignModal.css';

/**
 * BulkAutoPacingEditModal - Modal for editing bulk auto pacing schedule configuration
 */
const BulkAutoPacingEditModal = ({
  isOpen,
  onClose,
  schedule,
  onSubmit,
  isSubmitting
}) => {
  // Form state
  const [name, setName] = useState('');
  const [executionTime, setExecutionTime] = useState('09:00');
  const [timezone, setTimezone] = useState('America/Los_Angeles');
  const [flightStartDate, setFlightStartDate] = useState('');
  const [flightEndDate, setFlightEndDate] = useState('');
  const [minDailyBudget, setMinDailyBudget] = useState('1.00');

  // Track if form has been initialized to prevent polling from resetting form
  const formInitializedRef = useRef(false);

  // Populate form only when modal first opens, not on every schedule update from polling
  useEffect(() => {
    if (isOpen && schedule && !formInitializedRef.current) {
      setName(schedule.name || '');
      setExecutionTime(schedule.execution_time?.substring(0, 5) || '09:00');
      setTimezone(schedule.timezone || 'America/Los_Angeles');
      // Handle date formatting - could be Date object or string
      const startDate = schedule.flight_start_date;
      const endDate = schedule.flight_end_date;
      setFlightStartDate(startDate ? new Date(startDate).toISOString().split('T')[0] : '');
      setFlightEndDate(endDate ? new Date(endDate).toISOString().split('T')[0] : '');
      setMinDailyBudget(schedule.min_daily_budget?.toString() || '1.00');
      formInitializedRef.current = true;
    }

    // Reset the flag when modal closes so it re-initializes next time
    if (!isOpen) {
      formInitializedRef.current = false;
    }
  }, [schedule, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const scheduleData = {
      name: name.trim(),
      executionTime: `${executionTime}:00`,
      timezone,
      flightStartDate: flightStartDate || null,
      flightEndDate: flightEndDate || null,
      minDailyBudget: minDailyBudget ? parseFloat(minDailyBudget) : 1.00
    };

    onSubmit(scheduleData);
  };

  // Get today's date in local timezone (YYYY-MM-DD format)
  const getLocalToday = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isFormValid = () => {
    if (!name.trim()) return false;
    if (!flightStartDate || !flightEndDate) return false;
    const today = getLocalToday();
    if (flightStartDate < today) return false;
    if (flightEndDate < flightStartDate) return false;
    if (minDailyBudget && parseFloat(minDailyBudget) < 0) return false;
    return true;
  };

  if (!isOpen) return null;

  const todayLocal = getLocalToday();

  const timezones = [
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'UTC', label: 'UTC' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' }
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content edit-campaign-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <FontAwesomeIcon icon={faEdit} style={{ marginRight: '8px' }} />
            Edit Schedule Configuration
          </h2>
          <button className="close-button" onClick={onClose} type="button">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Schedule Name */}
            <div className="form-group">
              <label htmlFor="name">Schedule Name *</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="form-control"
                placeholder="Enter schedule name"
                required
              />
            </div>

            {/* Flight Period */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="flightStartDate">
                  <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '6px' }} />
                  Flight Start Date *
                </label>
                <input
                  type="date"
                  id="flightStartDate"
                  value={flightStartDate}
                  onChange={(e) => {
                    const newStartDate = e.target.value;
                    setFlightStartDate(newStartDate);
                    // If start date becomes later than end date, clear end date
                    if (flightEndDate && newStartDate > flightEndDate) {
                      setFlightEndDate('');
                    }
                  }}
                  className="form-control"
                  min={todayLocal}
                  max={flightEndDate || undefined}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="flightEndDate">
                  <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '6px' }} />
                  Flight End Date *
                </label>
                <input
                  type="date"
                  id="flightEndDate"
                  value={flightEndDate}
                  onChange={(e) => setFlightEndDate(e.target.value)}
                  className="form-control"
                  min={flightStartDate || todayLocal}
                  required
                />
              </div>
            </div>

            {/* Execution Time and Timezone */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="executionTime">
                  <FontAwesomeIcon icon={faClock} style={{ marginRight: '6px' }} />
                  Execution Time
                </label>
                <input
                  type="time"
                  id="executionTime"
                  value={executionTime}
                  onChange={(e) => setExecutionTime(e.target.value)}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label htmlFor="timezone">Timezone</label>
                <select
                  id="timezone"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="form-control"
                >
                  {timezones.map(tz => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Min Daily Budget */}
            <div className="form-group">
              <label htmlFor="minDailyBudget">Minimum Daily Budget ($)</label>
              <input
                type="number"
                id="minDailyBudget"
                value={minDailyBudget}
                onChange={(e) => setMinDailyBudget(e.target.value)}
                className="form-control"
                min="0"
                step="0.01"
                placeholder="1.00"
              />
              <small className="form-text text-muted">
                The minimum daily budget to apply when calculated budget is too low
              </small>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting || !isFormValid()}
            >
              {isSubmitting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkAutoPacingEditModal;

